import axiosInstance from '../utils/axiosConfig';

/**
 * Service for fetching entity options from the Corporate Structure module.
 *
 * The backend endpoint returns CorporateEntity records that have a linked
 * HMSEntity.  We map the response so that `value` = hms_entity_id, keeping
 * full backward compatibility with every invoicing API that expects an
 * HMSEntity FK.
 *
 * On API failure (or an empty list) we return an empty array and dispatch a
 * `hms-entities-load-failed` event. Row 128 (ported from HMS): nothing
 * listened to that event, so company pickers showed empty as if there were no
 * companies. `ui/CompanyListFailedBanner` (mounted once by Layout, so on every
 * screen) now listens and says the list couldn't load; a later successful load
 * dispatches `hms-entities-loaded` and clears it.
 */

let hmsEntitiesCache = null;
let hmsEntitiesFetchPromise = null;

const dispatchLoadFailure = (error) => {
  try {
    if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
      window.dispatchEvent(new CustomEvent('hms-entities-load-failed', {
        detail: {
          message: error?.message || 'Failed to load IMT entities',
          status: error?.response?.status ?? null,
        },
      }));
    }
  } catch (_) {
    // dispatchEvent must never break the calling component
  }
};

const dispatchLoaded = () => {
  try {
    if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
      window.dispatchEvent(new CustomEvent('hms-entities-loaded'));
    }
  } catch (_) {
    // dispatchEvent must never break the calling component
  }
};

/**
 * Fetches entity options from the corporate-structure endpoint.
 * Returns [{value: <hms_entity_id>, label: <entity_name>}] for all
 * active CorporateEntities that have a linked HMSEntity.
 */
export const fetchHMSEntities = async () => {
  if (hmsEntitiesCache !== null) {
    return hmsEntitiesCache;
  }

  if (hmsEntitiesFetchPromise !== null) {
    return hmsEntitiesFetchPromise;
  }

  hmsEntitiesFetchPromise = (async () => {
    try {
      const response = await axiosInstance.get(
        '/api/corporate-structure/entities/entity-options/'
      );

      const entities = (response.data || []).map((item) => ({
        value: item.hms_entity_id,
        label: item.label,
        corporate_entity_id: item.corporate_entity_id,
      }));

      if (entities.length > 0) {
        dispatchLoaded();
      } else {
        console.error('No entities returned from corporate-structure (check linked HMSEntity records)');
        dispatchLoadFailure(new Error('No IMT entities available'));
      }

      hmsEntitiesCache = entities;
      hmsEntitiesFetchPromise = null;
      return entities;
    } catch (error) {
      console.error('Error fetching entities from corporate-structure:', error);
      dispatchLoadFailure(error);

      // Do NOT cache the failure - allow retry on next caller
      hmsEntitiesFetchPromise = null;
      return [];
    }
  })();

  return hmsEntitiesFetchPromise;
};

/**
 * Clear the entities cache (useful for force refresh).
 */
export const clearHMSEntitiesCache = () => {
  hmsEntitiesCache = null;
  hmsEntitiesFetchPromise = null;
};

const hmsEntityService = {
  fetchHMSEntities,
  clearHMSEntitiesCache,
};

export default hmsEntityService;
